import React, { useState, useRef } from 'react';
import useSWR from 'swr';
import TabPageLayout, { PageLoader, DataCard, StatCard, EmptyState } from './TabPageLayout';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { 
  Plus, 
  Download, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Receipt,
  ArrowUpRight,
  ArrowDownRight,
  Upload,
  Loader2,
  Camera,
  Sparkles,
  RefreshCw,
  Trash2,
  PieChart,
  BarChart3,
  Percent,
  Wallet,
  CreditCard,
  Banknote,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import Webcam from 'react-webcam';

// Format currency with proper symbol
const formatCurrency = (amount, currency = 'USD') => {
  const symbols = { USD: '$', AED: 'AED ', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || '$';
  const absAmount = Math.abs(amount || 0);
  return `${amount < 0 ? '-' : ''}${symbol}${absAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format date nicely
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = parseInt(parts[2], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parts[0];
    return `${months[month]} ${day}, ${year}`;
  }
  return dateStr;
};

// Format percentage
const formatPercent = (value) => {
  if (!value || !isFinite(value)) return '0%';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

// Expense categories with icons and colors
const EXPENSE_CATEGORIES = {
  fuel: { label: 'Fuel', color: 'bg-orange-100 text-orange-700' },
  maintenance: { label: 'Maintenance', color: 'bg-blue-100 text-blue-700' },
  supplies: { label: 'Supplies', color: 'bg-green-100 text-green-700' },
  insurance: { label: 'Insurance', color: 'bg-purple-100 text-purple-700' },
  wages: { label: 'Wages', color: 'bg-pink-100 text-pink-700' },
  marketing: { label: 'Marketing', color: 'bg-cyan-100 text-cyan-700' },
  utilities: { label: 'Utilities', color: 'bg-yellow-100 text-yellow-700' },
  other: { label: 'Other', color: 'bg-gray-100 text-gray-700' }
};

const ProfitLossTab = () => {
  // View state
  const [activeView, setActiveView] = useState('overview'); // overview, revenue, expenses
  const [dateRange, setDateRange] = useState('month'); // week, month, year, all
  
  // Get org info
  const orgCountry = localStorage.getItem('orgCountry') || 'USA';
  const defaultCurrency = orgCountry === 'UAE' ? 'AED' : 'USD';
  
  // Expense form state
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [scanParsedData, setScanParsedData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [expenseForm, setExpenseForm] = useState({
    title: '',
    amount: '',
    currency: defaultCurrency,
    date: new Date().toISOString().split('T')[0],
    category: 'other',
    assetId: '',
    teamMemberId: '',
    notes: ''
  });

  // Calculate date range params
  const getDateParams = () => {
    const now = new Date();
    let startDate, endDate;
    
    if (dateRange === 'week') {
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];
    } else if (dateRange === 'month') {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];
    } else if (dateRange === 'year') {
      startDate = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];
    } else {
      startDate = '2020-01-01';
      endDate = now.toISOString().split('T')[0];
    }
    
    return { start_date: startDate, end_date: endDate };
  };

  // Fetcher
  const fetcher = async (url) => {
    const response = await api.get(url);
    return response.data;
  };

  // Fetch bookings for revenue
  const { data: bookingsData, isLoading: bookingsLoading, mutate: mutateBookings } = useSWR(
    '/bookings',
    fetcher,
    { revalidateOnFocus: true }
  );

  // Fetch expenses
  const { start_date, end_date } = getDateParams();
  const { data: expensesData, isLoading: expensesLoading, mutate: mutateExpenses } = useSWR(
    `/expenses?start_date=${start_date}&end_date=${end_date}&limit=100`,
    fetcher,
    { revalidateOnFocus: true }
  );

  // Fetch assets and team for expense form
  const { data: assetsData } = useSWR('/assets', fetcher);
  const { data: teamData } = useSWR('/team', fetcher);

  const assets = assetsData || [];
  const teamMembers = teamData || [];
  const expenses = expensesData?.expenses || [];

  // Process booking data for revenue - IMPROVED DATA INTEGRITY
  const processRevenue = () => {
    const allBookings = Array.isArray(bookingsData) ? bookingsData : bookingsData?.bookings || [];
    const { start_date, end_date } = getDateParams();
    
    // Filter by date range
    const filteredBookings = allBookings.filter(b => {
      const bookingDate = b.preferredDate || b.date || b.createdAt?.split('T')[0];
      return bookingDate >= start_date && bookingDate <= end_date;
    });
    
    // Separate paid bookings (confirmed with payment)
    const paidBookings = filteredBookings.filter(b => 
      (b.paymentStatus === 'paid') || 
      (b.status === 'paid') || 
      (b.status === 'confirmed' && b.paymentStatus === 'paid') ||
      (b.status === 'completed' && b.paymentStatus === 'paid')
    );
    
    // Separate refunded bookings
    const refundedBookings = filteredBookings.filter(b => 
      b.paymentStatus === 'refunded' || b.status === 'refunded'
    );
    
    // Calculate gross revenue - use totalAmount, fallback to paidAmount, then servicePrice
    const grossRevenue = paidBookings.reduce((sum, b) => {
      const amount = b.totalAmount || b.paidAmount || b.servicePrice || 0;
      return sum + amount;
    }, 0);
    
    // Calculate platform fees
    const platformFees = paidBookings.reduce((sum, b) => sum + (b.platformFee || 0), 0);
    
    // Calculate refunds - use refundAmount if available, otherwise totalAmount
    const totalRefunds = refundedBookings.reduce((sum, b) => {
      const refundAmt = b.refundAmount || b.totalAmount || 0;
      return sum + refundAmt;
    }, 0);
    
    // Net revenue after fees (before expenses)
    const netRevenue = grossRevenue - platformFees - totalRefunds;
    
    // Calculate average booking value
    const avgBookingValue = paidBookings.length > 0 ? grossRevenue / paidBookings.length : 0;
    
    return {
      bookings: paidBookings,
      refundedBookings,
      grossRevenue,
      platformFees,
      refunds: totalRefunds,
      netRevenue,
      bookingCount: paidBookings.length,
      refundCount: refundedBookings.length,
      avgBookingValue
    };
  };

  const revenue = processRevenue();
  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const netProfit = revenue.netRevenue - totalExpenses;
  const profitMargin = revenue.grossRevenue > 0 ? (netProfit / revenue.grossRevenue) * 100 : 0;

  // Group expenses by category
  const expensesByCategory = expenses.reduce((acc, e) => {
    const cat = e.category || 'other';
    if (!acc[cat]) acc[cat] = 0;
    acc[cat] += e.amount || 0;
    return acc;
  }, {});

  // Loading state
  const isLoading = bookingsLoading || expensesLoading;

  // Reset expense form
  const resetExpenseForm = () => {
    setExpenseForm({
      title: '',
      amount: '',
      currency: defaultCurrency,
      date: new Date().toISOString().split('T')[0],
      category: 'other',
      assetId: '',
      teamMemberId: '',
      notes: ''
    });
  };

  // Submit expense
  const handleSubmitExpense = async () => {
    if (!expenseForm.title || !expenseForm.amount) {
      toast.error('Title and amount are required');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await api.post('/expenses', {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
        assetId: expenseForm.assetId || null,
        teamMemberId: expenseForm.teamMemberId || null
      });
      toast.success('Expense added');
      setShowAddExpenseModal(false);
      setShowConfirmModal(false);
      resetExpenseForm();
      mutateExpenses();
    } catch (error) {
      toast.error('Failed to add expense');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete expense
  const handleDeleteExpense = async (id, title) => {
    if (!confirm(`Delete "${title}"?`)) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success('Expense deleted');
      mutateExpenses();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Camera/scan functions
  const startCamera = () => {
    setCameraActive(true);
    setShowScanModal(true);
  };

  const stopCamera = () => {
    setCameraActive(false);
    setShowScanModal(false);
  };

  const captureAndParse = async () => {
    if (!webcamRef.current) return;
    setScanLoading(true);
    try {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        toast.error('Failed to capture');
        setScanLoading(false);
        return;
      }
      stopCamera();
      await parseReceiptImage(imageSrc);
    } catch (error) {
      toast.error('Failed to capture');
      setScanLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    setScanLoading(true);
    const reader = new FileReader();
    reader.onload = async (e) => await parseReceiptImage(e.target?.result);
    reader.onerror = () => {
      toast.error('Failed to read file');
      setScanLoading(false);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const parseReceiptImage = async (base64Image) => {
    try {
      const response = await api.post('/expenses/parse-receipt', {
        image: base64Image,
        defaultCurrency
      });
      
      if (response.data.success) {
        const parsed = response.data.parsed;
        setScanParsedData(parsed);
        setExpenseForm({
          title: parsed.title || 'Receipt',
          amount: parsed.amount?.toString() || '',
          currency: parsed.currency || defaultCurrency,
          date: parsed.date || new Date().toISOString().split('T')[0],
          category: parsed.category || 'other',
          assetId: '',
          teamMemberId: '',
          notes: parsed.items?.length ? `Items: ${parsed.items.join(', ')}` : ''
        });
        setShowConfirmModal(true);
        toast.success('Receipt parsed!');
      } else {
        toast.error('Could not parse receipt');
        setShowAddExpenseModal(true);
      }
    } catch (error) {
      toast.error('Failed to parse receipt');
    } finally {
      setScanLoading(false);
    }
  };

  // Export expenses
  const handleExport = async () => {
    try {
      const { start_date, end_date } = getDateParams();
      const response = await api.get(`/expenses/export?start_date=${start_date}&end_date=${end_date}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expenses_${dateRange}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Exported!');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  // Get date range label
  const getDateRangeLabel = () => {
    switch (dateRange) {
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      case 'year': return 'This Year';
      default: return 'All Time';
    }
  };

  if (isLoading) {
    return (
      <TabPageLayout title="Profit & Loss">
        <PageLoader />
      </TabPageLayout>
    );
  }

  return (
    <TabPageLayout
      title="Profit & Loss"
      subtitle="Track revenue, expenses, and profitability"
      actions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            className="h-9"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button
            size="sm"
            onClick={() => { resetExpenseForm(); setShowAddExpenseModal(true); }}
            className="h-9 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Expense
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Period Selector */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {['week', 'month', 'year', 'all'].map((period) => (
              <button
                key={period}
                onClick={() => setDateRange(period)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  dateRange === period
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                } ${period !== 'week' ? 'border-l border-gray-200' : ''}`}
              >
                {period === 'all' ? 'All Time' : period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { mutateBookings(); mutateExpenses(); }}
            className="h-9"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Main P&L Summary Card */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-slate-400 text-sm">{getDateRangeLabel()} Performance</p>
              <h3 className="text-3xl font-bold mt-1">
                {formatCurrency(netProfit)}
              </h3>
              <p className={`text-sm mt-1 flex items-center gap-1 ${profitMargin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                <Percent className="w-3 h-3" />
                {formatPercent(profitMargin)} profit margin
              </p>
            </div>
            <div className={`w-20 h-20 rounded-full flex items-center justify-center ${netProfit >= 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
              {netProfit >= 0 ? (
                <TrendingUp className="w-10 h-10 text-emerald-400" />
              ) : (
                <TrendingDown className="w-10 h-10 text-red-400" />
              )}
            </div>
          </div>
          
          {/* P&L Breakdown Bar */}
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Gross Revenue</span>
              <span className="text-emerald-400 font-semibold">{formatCurrency(revenue.grossRevenue)}</span>
            </div>
            <div className="h-3 bg-slate-700 rounded-full overflow-hidden flex">
              {revenue.grossRevenue > 0 && (
                <>
                  <div 
                    className="bg-emerald-500 h-full transition-all"
                    style={{ width: `${((revenue.grossRevenue - revenue.platformFees - revenue.refunds - totalExpenses) / revenue.grossRevenue) * 100}%` }}
                    title={`Net Profit: ${formatCurrency(netProfit)}`}
                  />
                  <div 
                    className="bg-orange-500 h-full transition-all"
                    style={{ width: `${(revenue.platformFees / revenue.grossRevenue) * 100}%` }}
                    title={`Platform Fees: ${formatCurrency(revenue.platformFees)}`}
                  />
                  <div 
                    className="bg-red-500 h-full transition-all"
                    style={{ width: `${(totalExpenses / revenue.grossRevenue) * 100}%` }}
                    title={`Expenses: ${formatCurrency(totalExpenses)}`}
                  />
                  {revenue.refunds > 0 && (
                    <div 
                      className="bg-yellow-500 h-full transition-all"
                      style={{ width: `${(revenue.refunds / revenue.grossRevenue) * 100}%` }}
                      title={`Refunds: ${formatCurrency(revenue.refunds)}`}
                    />
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                <span className="text-slate-400">Net Profit</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                <span className="text-slate-400">Fees</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <span className="text-slate-400">Expenses</span>
              </div>
              {revenue.refunds > 0 && (
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                  <span className="text-slate-400">Refunds</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Gross Revenue</p>
                <p className="text-lg font-bold text-gray-900">{formatCurrency(revenue.grossRevenue)}</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">{revenue.bookingCount} bookings</p>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Platform Fees</p>
                <p className="text-lg font-bold text-gray-900">{formatCurrency(revenue.platformFees)}</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">5.9% + $0.30 per txn</p>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Expenses</p>
                <p className="text-lg font-bold text-gray-900">{formatCurrency(totalExpenses)}</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">{expenses.length} entries</p>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Avg Booking</p>
                <p className="text-lg font-bold text-gray-900">{formatCurrency(revenue.avgBookingValue)}</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">Per transaction</p>
          </div>
        </div>

        {/* Refunds Alert */}
        {revenue.refunds > 0 && (
          <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-yellow-800">
                {revenue.refundCount} refund{revenue.refundCount !== 1 ? 's' : ''} totaling {formatCurrency(revenue.refunds)}
              </p>
              <p className="text-sm text-yellow-700">This amount has been deducted from your net revenue</p>
            </div>
          </div>
        )}

        {/* View Toggle */}
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {[
              { id: 'overview', label: 'Overview', icon: PieChart },
              { id: 'revenue', label: 'Revenue', icon: TrendingUp },
              { id: 'expenses', label: 'Expenses', icon: Receipt }
            ].map((view) => {
              const Icon = view.icon;
              return (
                <button
                  key={view.id}
                  onClick={() => setActiveView(view.id)}
                  className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeView === view.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  } ${view.id !== 'overview' ? 'border-l border-gray-200' : ''}`}
                >
                  <Icon className="w-4 h-4" />
                  {view.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Overview View */}
        {activeView === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Summary */}
            <DataCard title="Revenue Breakdown" subtitle={getDateRangeLabel()}>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-green-600" />
                    <span className="text-gray-700">Gross Revenue</span>
                  </div>
                  <span className="font-semibold text-green-600">{formatCurrency(revenue.grossRevenue)}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-orange-600" />
                    <span className="text-gray-700">Platform Fees</span>
                  </div>
                  <span className="font-medium text-orange-600">-{formatCurrency(revenue.platformFees)}</span>
                </div>
                {revenue.refunds > 0 && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <ArrowDownRight className="w-4 h-4 text-yellow-600" />
                      <span className="text-gray-700">Refunds ({revenue.refundCount})</span>
                    </div>
                    <span className="font-medium text-yellow-600">-{formatCurrency(revenue.refunds)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-3 bg-green-50 px-4 rounded-lg -mx-1">
                  <span className="font-semibold text-gray-900">Net Revenue</span>
                  <span className="font-bold text-green-600 text-lg">{formatCurrency(revenue.netRevenue)}</span>
                </div>
              </div>
            </DataCard>

            {/* Expenses Summary */}
            <DataCard 
              title="Expenses Summary" 
              subtitle={getDateRangeLabel()}
              actions={
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={scanLoading}
                    className="h-8"
                    title="Upload receipt"
                  >
                    {scanLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={startCamera}
                    className="h-8"
                    title="Scan receipt"
                  >
                    <Camera className="w-4 h-4" />
                  </Button>
                </div>
              }
            >
              {expenses.length === 0 ? (
                <div className="text-center py-8">
                  <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500 text-sm mb-1">No expenses recorded</p>
                  <p className="text-gray-400 text-xs mb-4">Add expenses to track your profit margin</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { resetExpenseForm(); setShowAddExpenseModal(true); }}
                    className="h-9"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Expense
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Category breakdown */}
                  {Object.keys(expensesByCategory).length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {Object.entries(expensesByCategory).map(([cat, amount]) => {
                        const catInfo = EXPENSE_CATEGORIES[cat] || EXPENSE_CATEGORIES.other;
                        return (
                          <span key={cat} className={`px-2 py-1 rounded-full text-xs font-medium ${catInfo.color}`}>
                            {catInfo.label}: {formatCurrency(amount)}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  
                  {expenses.slice(0, 5).map((expense) => (
                    <div key={expense.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{expense.title}</p>
                        <p className="text-xs text-gray-500">{formatDate(expense.date)}</p>
                      </div>
                      <span className="font-semibold text-red-600">-{formatCurrency(expense.amount, expense.currency)}</span>
                    </div>
                  ))}
                  {expenses.length > 5 && (
                    <button
                      onClick={() => setActiveView('expenses')}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium w-full text-center py-2"
                    >
                      View all {expenses.length} expenses →
                    </button>
                  )}
                  <div className="flex justify-between items-center py-3 bg-red-50 px-4 rounded-lg -mx-1 mt-3">
                    <span className="font-semibold text-gray-900">Total Expenses</span>
                    <span className="font-bold text-red-600 text-lg">-{formatCurrency(totalExpenses)}</span>
                  </div>
                </div>
              )}
            </DataCard>
          </div>
        )}

        {/* Revenue View */}
        {activeView === 'revenue' && (
          <DataCard title="Revenue Details" subtitle={`${revenue.bookingCount} paid bookings`}>
            {revenue.bookings.length === 0 ? (
              <EmptyState
                icon={<TrendingUp className="w-12 h-12" />}
                title="No revenue yet"
                description="Revenue will appear here when bookings are paid"
              />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Fees</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {revenue.bookings.slice(0, 20).map((booking) => {
                      const amount = booking.totalAmount || booking.paidAmount || booking.servicePrice || 0;
                      const fees = booking.platformFee || 0;
                      const net = amount - fees;
                      return (
                        <TableRow key={booking.id}>
                          <TableCell className="text-gray-500">{formatDate(booking.preferredDate || booking.date)}</TableCell>
                          <TableCell className="font-medium">{booking.customerName || 'Customer'}</TableCell>
                          <TableCell className="text-gray-600">{booking.serviceName || 'Service'}</TableCell>
                          <TableCell className="text-right text-green-600">{formatCurrency(amount)}</TableCell>
                          <TableCell className="text-right text-orange-600">-{formatCurrency(fees)}</TableCell>
                          <TableCell className="text-right font-semibold text-green-700">{formatCurrency(net)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {revenue.bookings.length > 20 && (
                  <p className="text-center text-sm text-gray-500 py-4">
                    Showing 20 of {revenue.bookings.length} bookings
                  </p>
                )}
              </div>
            )}
          </DataCard>
        )}

        {/* Expenses View */}
        {activeView === 'expenses' && (
          <DataCard 
            title="Expense Details" 
            subtitle={`${expenses.length} entries`}
            actions={
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={scanLoading}
                  className="h-9"
                >
                  {scanLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={startCamera}
                  className="h-9"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Scan
                </Button>
              </div>
            }
          >
            {expenses.length === 0 ? (
              <EmptyState
                icon={<Receipt className="w-12 h-12" />}
                title="No expenses recorded"
                description="Add expenses manually or scan receipts"
                action={
                  <Button
                    onClick={() => { resetExpenseForm(); setShowAddExpenseModal(true); }}
                    className="bg-blue-600 hover:bg-blue-700 text-white h-10"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Expense
                  </Button>
                }
              />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenses.map((expense) => {
                      const catInfo = EXPENSE_CATEGORIES[expense.category] || EXPENSE_CATEGORIES.other;
                      return (
                        <TableRow key={expense.id} className="group">
                          <TableCell className="text-gray-500">{formatDate(expense.date)}</TableCell>
                          <TableCell>
                            <p className="font-medium">{expense.title}</p>
                            {expense.notes && <p className="text-xs text-gray-400 truncate max-w-xs">{expense.notes}</p>}
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${catInfo.color}`}>
                              {catInfo.label}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-red-600">
                            -{formatCurrency(expense.amount, expense.currency)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500"
                              onClick={() => handleDeleteExpense(expense.id, expense.title)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </DataCard>
        )}
      </div>

      {/* Add Expense Modal */}
      <Dialog open={showAddExpenseModal} onOpenChange={(open) => { if (!open) { setShowAddExpenseModal(false); resetExpenseForm(); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Expense</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title <span className="text-red-500">*</span></Label>
              <Input
                value={expenseForm.title}
                onChange={(e) => setExpenseForm({ ...expenseForm, title: e.target.value })}
                placeholder="e.g., Fuel, Supplies"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Amount <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={expenseForm.currency} onValueChange={(v) => setExpenseForm({ ...expenseForm, currency: v })}>
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">$ USD</SelectItem>
                    <SelectItem value="AED">AED</SelectItem>
                    <SelectItem value="EUR">€ EUR</SelectItem>
                    <SelectItem value="GBP">£ GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={expenseForm.date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}>
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EXPENSE_CATEGORIES).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Asset (optional)</Label>
                <Select value={expenseForm.assetId} onValueChange={(v) => setExpenseForm({ ...expenseForm, assetId: v === 'none' ? '' : v })}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {assets.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Team Member (optional)</Label>
                <Select value={expenseForm.teamMemberId} onValueChange={(v) => setExpenseForm({ ...expenseForm, teamMemberId: v === 'none' ? '' : v })}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {teamMembers.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                placeholder="Additional details"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowAddExpenseModal(false); resetExpenseForm(); }} className="h-10">
              Cancel
            </Button>
            <Button onClick={handleSubmitExpense} disabled={isSubmitting} className="h-10 bg-blue-600 hover:bg-blue-700 text-white">
              {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scan Receipt Modal */}
      <Dialog open={showScanModal} onOpenChange={(open) => { if (!open) stopCamera(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Scan Receipt
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative bg-black rounded-xl overflow-hidden aspect-[4/3]">
              {cameraActive && (
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  screenshotFormat="image/jpeg"
                  screenshotQuality={0.8}
                  videoConstraints={{ facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }}
                  className="w-full h-full object-cover"
                  onUserMediaError={() => { toast.error('Camera access denied'); stopCamera(); }}
                />
              )}
              {!cameraActive && (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-100 rounded-lg">
              <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <p className="text-xs text-amber-700">AI parsing is ~99% accurate. Please verify the result.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={stopCamera} className="h-10">Cancel</Button>
            <Button onClick={captureAndParse} disabled={scanLoading || !cameraActive} className="h-10 bg-blue-600 hover:bg-blue-700 text-white">
              {scanLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
              Capture
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Parsed Receipt Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Receipt</DialogTitle>
          </DialogHeader>
          {scanParsedData && (
            <div className="p-2 bg-green-50 rounded-lg text-xs text-green-700 mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <span>AI Parsed ({scanParsedData.confidence} confidence) — please verify</span>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label>Title <span className="text-red-500">*</span></Label>
              <Input value={expenseForm.title} onChange={(e) => setExpenseForm({ ...expenseForm, title: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Amount <span className="text-red-500">*</span></Label>
                <Input type="number" step="0.01" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={expenseForm.currency} onValueChange={(v) => setExpenseForm({ ...expenseForm, currency: v })}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">$ USD</SelectItem>
                    <SelectItem value="AED">AED</SelectItem>
                    <SelectItem value="EUR">€ EUR</SelectItem>
                    <SelectItem value="GBP">£ GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={expenseForm.date} onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowConfirmModal(false); resetExpenseForm(); }} className="h-10">Cancel</Button>
            <Button onClick={handleSubmitExpense} disabled={isSubmitting} className="h-10 bg-blue-600 hover:bg-blue-700 text-white">
              {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
              Add Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loading Overlay */}
      {scanLoading && !showScanModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 flex flex-col items-center gap-3 shadow-xl">
            <div className="relative">
              <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
              <Sparkles className="w-4 h-4 text-amber-400 absolute -top-1 -right-1 animate-pulse" />
            </div>
            <p className="font-medium text-gray-700">Parsing receipt...</p>
            <p className="text-xs text-gray-400">AI is analyzing your image</p>
          </div>
        </div>
      )}
    </TabPageLayout>
  );
};

export default ProfitLossTab;
